import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, MapPin, DollarSign, Calendar, Ruler, ArrowRight,
  Lock, LogIn, ChevronLeft, ChevronRight, Loader2, Globe, Filter,
  Building2, Briefcase, RefreshCw, X, Map, LayoutGrid, List,
  BadgeCheck, SlidersHorizontal, ChevronDown, ArrowUpDown
} from 'lucide-react';
import axios from 'axios';

// Leaflet map imports
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Budget color coding ──────────────────────────────────────────────────────
const CURRENCY_TO_USD = { USD: 1, EUR: 1.1, GBP: 1.25, AED: 0.27, TRY: 0.032 };
function toUSD(amount, currency) {
  return amount * (CURRENCY_TO_USD[currency] || 1);
}
function getBudgetColor(budgetMin, budgetMax, currency) {
  const avgUSD = toUSD((budgetMin + budgetMax) / 2, currency);
  if (avgUSD <= 20000) return '#F97316';   // orange
  if (avgUSD <= 50000) return '#3B82F6';   // blue
  if (avgUSD <= 100000) return '#22C55E';  // green
  if (avgUSD <= 200000) return '#EF4444';  // red
  return '#D3AF37';                        // metallic gold
}
function getBudgetLabel(budgetMin, budgetMax, currency) {
  const avgUSD = toUSD((budgetMin + budgetMax) / 2, currency);
  if (avgUSD <= 20000) return { label: '< $20K', color: 'orange' };
  if (avgUSD <= 50000) return { label: '$20K–$50K', color: 'blue' };
  if (avgUSD <= 100000) return { label: '$50K–$100K', color: 'green' };
  if (avgUSD <= 200000) return { label: '$100K–$200K', color: 'red' };
  return { label: '$200K+', color: 'gold' };
}
function getBudgetTierKey(budgetMin, budgetMax, currency) {
  const avgUSD = toUSD((budgetMin + budgetMax) / 2, currency);
  if (avgUSD <= 20000) return '<20k';
  if (avgUSD <= 50000) return '20-50k';
  if (avgUSD <= 100000) return '50-100k';
  if (avgUSD <= 200000) return '100-200k';
  return '200k+';
}

const CURRENCY_SYMBOL = { USD: '$', EUR: '€', GBP: '£', AED: 'AED', TRY: '₺' };
const COUNTRY_FLAG = {
  DE: '🇩🇪', TR: '🇹🇷', AE: '🇦🇪', ES: '🇪🇸', NL: '🇳🇱',
  FR: '🇫🇷', GB: '🇬🇧', US: '🇺🇸', SG: '🇸🇬', IT: '🇮🇹',
  CN: '🇨🇳', JP: '🇯🇵', AU: '🇦🇺', CA: '🇨🇦', BR: '🇧🇷',
  AT: '🇦🇹', CH: '🇨🇭', BE: '🇧🇪', PL: '🇵🇱', CZ: '🇨🇿', HU: '🇭🇺',
};

// Country full names for text search
const COUNTRY_NAMES = {
  DE: 'germany deutschland', TR: 'turkey türkiye', AE: 'uae united arab emirates dubai',
  ES: 'spain españa', NL: 'netherlands holland', FR: 'france', GB: 'uk united kingdom britain england',
  US: 'usa united states america', SG: 'singapore', IT: 'italy italia', JP: 'japan',
  CN: 'china', AU: 'australia', CA: 'canada', BR: 'brazil brasil',
  AT: 'austria wien', CH: 'switzerland suisse', BE: 'belgium belgique',
  PL: 'poland polska', CZ: 'czech republic czechia', HU: 'hungary',
};

// City → venue aliases for convention center search
const CITY_VENUES = {
  'Frankfurt': 'messe frankfurt festhalle',
  'Hannover': 'hannover messe',
  'Munich': 'messe münchen munich trade fair',
  'München': 'messe münchen munich trade fair',
  'Cologne': 'koelnmesse', 'Köln': 'koelnmesse',
  'Düsseldorf': 'messe düsseldorf',
  'Stuttgart': 'messe stuttgart',
  'Berlin': 'messe berlin',
  'Dubai': 'dwtc world trade centre',
  'Abu Dhabi': 'adnec',
  'Istanbul': 'cnr expo cnr fuar beylikdüzü',
  'İstanbul': 'cnr expo cnr fuar beylikdüzü',
  'Barcelona': 'fira de barcelona gran via montjuic',
  'Madrid': 'ifema',
  'Milan': 'fiera milano rho', 'Milano': 'fiera milano rho',
  'Paris': 'paris expo porte de versailles le bourget',
  'Las Vegas': 'las vegas convention center lvcc sands expo',
  'Singapore': 'marina bay sands suntec singapore expo',
  'Tokyo': 'tokyo big sight makuhari messe',
  'Osaka': 'intex osaka',
  'Shanghai': 'sniec national exhibition',
  'Birmingham': 'nec national exhibition centre',
  'London': 'excel london olympia earls court',
  'Amsterdam': 'rai amsterdam',
  'Vienna': 'messe wien', 'Wien': 'messe wien',
  'Brussels': 'brussels expo', 'Basel': 'messe basel',
  'Bologna': 'bolognafiere', 'Rimini': 'rimini expo',
  'Geneva': 'palexpo',
};

const PAGE_SIZE = 30;

function formatMonth(ym) {
  if (!ym) return '—';
  const [y, m] = ym.split('-');
  const d = new Date(parseInt(y), parseInt(m) - 1);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatBudget(min, max, currency) {
  const sym = CURRENCY_SYMBOL[currency] || currency;
  const fmt = (n) => n >= 1000 ? `${Math.round(n / 1000)}K` : String(n);
  return `${sym}${fmt(min)} – ${sym}${fmt(max)}`;
}

// ─── Login Prompt Modal ───────────────────────────────────────────────────────
function LoginPromptModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center"
        onClick={e => e.stopPropagation()}
      >
        <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Lock className="w-7 h-7 text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Access Restricted</h2>
        <p className="text-gray-500 text-sm mb-6">
          Sign in or create a free account to see show names and submit proposals.
        </p>
        <div className="flex flex-col gap-3">
          <Link to="/admin-login" className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors">
            <LogIn className="w-4 h-4" /> Sign In
          </Link>
          <Link to="/contractor/signup" className="flex items-center justify-center gap-2 border border-green-600 text-green-700 hover:bg-green-50 font-semibold py-3 px-6 rounded-xl transition-colors">
            <Building2 className="w-4 h-4" /> Sign Up Free
          </Link>
          <button onClick={onClose} className="text-sm text-gray-400 hover:text-gray-600 mt-1">Close</button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── KPI Bar ──────────────────────────────────────────────────────────────────
function KPIBar({ briefs, total, isVerified = false }) {
  const stats = useMemo(() => {
    const src = briefs;
    const countries = [...new Set(src.map(b => b.country).filter(Boolean))];
    const cities = [...new Set(src.map(b => b.city).filter(Boolean))];
    const totalUSD = src.reduce((s, b) => s + toUSD((b.budget_min + b.budget_max) / 2, b.currency), 0);
    const avgBudget = src.length > 0 ? totalUSD / src.length : 0;
    return { total: total || src.length, countries: countries.length, cities: cities.length, avgBudget };
  }, [briefs, total]);

  const goldBg = '#FAF6D0';
  const goldColor = '#D3AF37';

  const KPI_ITEMS = isVerified ? [
    { label: 'Verified Projects', value: stats.total, icon: BadgeCheck, bg: goldBg, ic: goldColor },
    { label: 'Countries', value: stats.countries, icon: Globe, bg: '#F0FDF4', ic: '#22C55E' },
    { label: 'Cities', value: stats.cities, icon: MapPin, bg: '#FAF5FF', ic: '#A855F7' },
    { label: 'Avg Budget (Verified)', value: `$${Math.round(stats.avgBudget / 1000)}K`, icon: DollarSign, bg: goldBg, ic: goldColor },
  ] : [
    { label: 'Total Projects', value: stats.total, icon: Briefcase, bg: '#EFF6FF', ic: '#3B82F6' },
    { label: 'Countries', value: stats.countries, icon: Globe, bg: '#F0FDF4', ic: '#22C55E' },
    { label: 'Cities', value: stats.cities, icon: MapPin, bg: '#FAF5FF', ic: '#A855F7' },
    { label: 'Avg Budget', value: `$${Math.round(stats.avgBudget / 1000)}K`, icon: DollarSign, bg: '#FFFBEB', ic: '#F59E0B' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {KPI_ITEMS.map(k => {
        const Icon = k.icon;
        return (
          <div
            key={k.label}
            className="bg-white rounded-2xl border shadow-sm p-4 flex items-center gap-4"
            style={{ borderColor: isVerified ? '#E8D080' : '#F1F5F9' }}
          >
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: k.bg }}>
              <Icon className="w-5 h-5" style={{ color: k.ic }} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 leading-none">{k.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{k.label}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Advanced Filter Bar ──────────────────────────────────────────────────────
function AdvancedFilterBar({ searchText, setSearchText, countryFilter, setCountryFilter, budgetTier, setBudgetTier, sortBy, setSortBy, standSizeFilter, setStandSizeFilter, onReset, filteredCount, totalCount }) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const BUDGET_TIERS = [
    { value: 'all', label: 'All Budgets' },
    { value: '<20k', label: '< $20K', color: '#F97316' },
    { value: '20-50k', label: '$20K – $50K', color: '#3B82F6' },
    { value: '50-100k', label: '$50K – $100K', color: '#22C55E' },
    { value: '100-200k', label: '$100K – $200K', color: '#EF4444' },
    { value: '200k+', label: '$200K+', color: '#D3AF37' },
  ];

  const STAND_SIZES = [
    { value: 'all', label: 'Any Size' },
    { value: '<50', label: '< 50 m²' },
    { value: '50-100', label: '50 – 100 m²' },
    { value: '100-200', label: '100 – 200 m²' },
    { value: '200+', label: '200+ m²' },
  ];

  const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest First' },
    { value: 'budget_high', label: 'Budget: High → Low' },
    { value: 'budget_low', label: 'Budget: Low → High' },
    { value: 'deadline', label: 'Deadline: Soonest' },
  ];

  const hasActiveFilters = searchText || countryFilter || budgetTier !== 'all' || sortBy !== 'newest' || standSizeFilter !== 'all';

  return (
    <div className="bg-white border-b border-gray-100 px-4 py-3 shadow-sm">
      <div className="max-w-7xl mx-auto space-y-3">
        {/* Main search row */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Search input */}
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search by city, country, venue or show name..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent"
              data-testid="filter-search-text"
            />
          </div>

          {/* Country select */}
          <select
            value={countryFilter}
            onChange={e => setCountryFilter(e.target.value)}
            className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 bg-white min-w-[130px]"
            data-testid="filter-country"
          >
            <option value="">All Countries</option>
            <option value="DE">🇩🇪 Germany</option>
            <option value="TR">🇹🇷 Türkiye</option>
            <option value="AE">🇦🇪 UAE</option>
            <option value="ES">🇪🇸 Spain</option>
            <option value="NL">🇳🇱 Netherlands</option>
            <option value="FR">🇫🇷 France</option>
            <option value="GB">🇬🇧 UK</option>
            <option value="US">🇺🇸 USA</option>
            <option value="SG">🇸🇬 Singapore</option>
            <option value="IT">🇮🇹 Italy</option>
            <option value="JP">🇯🇵 Japan</option>
            <option value="CN">🇨🇳 China</option>
            <option value="AT">🇦🇹 Austria</option>
            <option value="CH">🇨🇭 Switzerland</option>
            <option value="BE">🇧🇪 Belgium</option>
            <option value="PL">🇵🇱 Poland</option>
          </select>

          {/* Advanced toggle */}
          <button
            onClick={() => setShowAdvanced(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${showAdvanced ? 'bg-green-50 border-green-300 text-green-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
            data-testid="filter-advanced-toggle"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-green-500" />}
            <ChevronDown className={`w-3 h-3 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
          </button>

          {/* Sort */}
          <div className="flex items-center gap-1.5 ml-auto">
            <ArrowUpDown className="w-4 h-4 text-gray-400 shrink-0" />
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-400 bg-white"
              data-testid="filter-sort"
            >
              {SORT_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {/* Clear */}
          {hasActiveFilters && (
            <button
              onClick={onReset}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-500 transition-colors px-2"
            >
              <X className="w-4 h-4" />Clear
            </button>
          )}
        </div>

        {/* Advanced panel */}
        {showAdvanced && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 pb-1 border-t border-gray-100"
          >
            {/* Budget tier */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Budget Range</p>
              <div className="flex flex-wrap gap-1.5">
                {BUDGET_TIERS.map(tier => (
                  <button
                    key={tier.value}
                    onClick={() => setBudgetTier(tier.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${budgetTier === tier.value ? 'border-transparent text-white shadow-sm' : 'border-gray-200 text-gray-600 bg-white hover:border-gray-300'}`}
                    style={budgetTier === tier.value && tier.color ? { backgroundColor: tier.color, borderColor: tier.color } : {}}
                    data-testid={`filter-budget-${tier.value}`}
                  >
                    {tier.color && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: budgetTier === tier.value ? 'white' : tier.color }} />}
                    {tier.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Stand size */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Stand Size</p>
              <div className="flex flex-wrap gap-1.5">
                {STAND_SIZES.map(sz => (
                  <button
                    key={sz.value}
                    onClick={() => setStandSizeFilter(sz.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${standSizeFilter === sz.value ? 'bg-green-600 text-white border-green-600 shadow-sm' : 'border-gray-200 text-gray-600 bg-white hover:border-gray-300'}`}
                    data-testid={`filter-stand-${sz.value}`}
                  >
                    {sz.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Result count */}
        {(hasActiveFilters || true) && (
          <div className="flex items-center justify-between text-xs text-gray-400 pt-0.5">
            <span>
              Showing <span className="font-semibold text-gray-700">{filteredCount}</span> of {totalCount} projects
            </span>
            {hasActiveFilters && (
              <span className="text-green-600 font-medium">Filters active</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Brief Card (Grid) ────────────────────────────────────────────────────────
function BriefCard({ brief, isLoggedIn, onLockedClick, index, verified = false }) {
  const flag = COUNTRY_FLAG[brief.country] || '🌍';
  const markerColor = verified ? '#D3AF37' : getBudgetColor(brief.budget_min || 0, brief.budget_max || 0, brief.currency);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      onClick={!isLoggedIn ? onLockedClick : undefined}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden group cursor-pointer relative"
      data-testid={`brief-card-${brief.id}`}
    >
      {verified && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm" style={{ backgroundColor: '#D3AF37' }}>
          <BadgeCheck className="w-3.5 h-3.5" />Verified
        </div>
      )}
      <div className="h-1" style={{ backgroundColor: markerColor }} />
      <div className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{flag}</span>
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">{brief.country}</p>
              <p className="text-sm font-semibold text-gray-800">{brief.city}</p>
            </div>
          </div>
          {brief.proposals_count > 0 && (
            <span className="text-xs bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded-full">
              {brief.proposals_count} proposals
            </span>
          )}
        </div>
        <div className="mb-4">
          <p className="text-xs text-gray-400 mb-1">Show Name</p>
          <div className="flex items-center gap-2">
            <p className="text-base font-bold text-gray-900 group-hover:text-green-700 transition-colors">
              {brief.show_masked}
            </p>
            {!isLoggedIn && <Lock className="w-3.5 h-3.5 text-gray-300" />}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-gray-50 rounded-xl p-3">
            <div className="flex items-center gap-1.5 text-gray-400 mb-1">
              <Calendar className="w-3.5 h-3.5" />
              <p className="text-xs">Show Date</p>
            </div>
            <p className="text-sm font-semibold text-gray-800">{formatMonth(brief.month)}</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3">
            <div className="flex items-center gap-1.5 text-gray-400 mb-1">
              <Ruler className="w-3.5 h-3.5" />
              <p className="text-xs">Stand Size</p>
            </div>
            <p className="text-sm font-semibold text-gray-800">
              {brief.stand_w && brief.stand_d ? `${brief.stand_w}×${brief.stand_d}m` : brief.stand_area ? `${brief.stand_area}m²` : '—'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 p-3 rounded-xl" style={{ backgroundColor: markerColor + '18' }}>
          <DollarSign className="w-4 h-4 shrink-0" style={{ color: markerColor }} />
          <div>
            <p className="text-xs font-medium" style={{ color: markerColor }}>Budget Range</p>
            <p className="text-sm font-bold text-gray-800">{formatBudget(brief.budget_min, brief.budget_max, brief.currency)}</p>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-gray-400">
            Deadline: {brief.deadline_at ? new Date(brief.deadline_at).toLocaleDateString('en-GB') : '—'}
          </p>
          {isLoggedIn ? (
            <Link to="/admin-login" onClick={e => e.stopPropagation()} className="flex items-center gap-1 text-xs font-semibold text-green-700 hover:text-green-900 transition-colors">
              Submit Proposal <ArrowRight className="w-3 h-3" />
            </Link>
          ) : (
            <span className="flex items-center gap-1 text-xs font-semibold text-gray-400">
              <Lock className="w-3 h-3" /> Login Required
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Brief Row (List) ─────────────────────────────────────────────────────────
function BriefRow({ brief, isLoggedIn, onLockedClick, index, verified = false }) {
  const flag = COUNTRY_FLAG[brief.country] || '🌍';
  const markerColor = verified ? '#D3AF37' : getBudgetColor(brief.budget_min || 0, brief.budget_max || 0, brief.currency);
  const { label: budgetLabel } = getBudgetLabel(brief.budget_min || 0, brief.budget_max || 0, brief.currency);

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.02 }}
      onClick={!isLoggedIn ? onLockedClick : undefined}
      className={`flex items-center gap-4 p-4 bg-white rounded-xl border shadow-sm hover:shadow-md transition-all cursor-pointer group ${verified ? 'border-amber-200 hover:border-amber-400' : 'border-gray-100 hover:border-green-200'}`}
      data-testid={`brief-row-${brief.id}`}
    >
      <div className="w-1 h-12 rounded-full shrink-0" style={{ backgroundColor: markerColor }} />
      <div className="flex items-center gap-2 w-28 shrink-0">
        <span className="text-xl">{flag}</span>
        <div>
          <p className="text-xs text-gray-400">{brief.country}</p>
          <p className="text-sm font-semibold text-gray-800">{brief.city}</p>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-400 mb-0.5">Show</p>
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-bold text-gray-900 group-hover:text-green-700 truncate">{brief.show_masked}</p>
          {!isLoggedIn && <Lock className="w-3 h-3 text-gray-300 shrink-0" />}
        </div>
      </div>
      {verified && (
        <div className="hidden sm:flex items-center gap-1 text-white text-xs font-bold px-2.5 py-1 rounded-full shrink-0" style={{ backgroundColor: '#D3AF37' }}>
          <BadgeCheck className="w-3.5 h-3.5" />Verified
        </div>
      )}
      <div className="hidden sm:block w-28 shrink-0">
        <p className="text-xs text-gray-400 mb-0.5">Show Date</p>
        <p className="text-sm font-medium text-gray-700">{formatMonth(brief.month)}</p>
      </div>
      <div className="hidden md:block w-20 shrink-0">
        <p className="text-xs text-gray-400 mb-0.5">Stand</p>
        <p className="text-sm font-medium text-gray-700">
          {brief.stand_w && brief.stand_d ? `${brief.stand_w}×${brief.stand_d}m` : '—'}
        </p>
      </div>
      <div className="shrink-0">
        <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full" style={{ backgroundColor: markerColor + '1A', color: markerColor }}>
          {budgetLabel}
        </span>
        <p className="text-xs text-gray-500 text-center mt-0.5">{formatBudget(brief.budget_min, brief.budget_max, brief.currency)}</p>
      </div>
      <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-green-500 transition-colors shrink-0" />
    </motion.div>
  );
}

// ─── Budget Legend ─────────────────────────────────────────────────────────────
function BudgetLegend() {
  const items = [
    { color: '#F97316', label: '< $20K' },
    { color: '#3B82F6', label: '$20K – $50K' },
    { color: '#22C55E', label: '$50K – $100K' },
    { color: '#EF4444', label: '$100K – $200K' },
    { color: '#D3AF37', label: '$200K+' },
  ];
  return (
    <div className="absolute bottom-6 left-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100 px-3 py-2.5">
      <p className="text-xs font-bold text-gray-600 mb-2 uppercase tracking-wide">Budget Range</p>
      <div className="space-y-1.5">
        {items.map(item => (
          <div key={item.label} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
            <span className="text-xs text-gray-600">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Map Dashboard (no KPI — shown separately) ───────────────────────────────
function MapDashboard({ briefs, isLoggedIn, onLockedClick, onViewAll }) {
  const withCoords = briefs.filter(b => b.lat && b.lng);

  const last24h = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return briefs.filter(b => b.created_at && new Date(b.created_at).getTime() > cutoff);
  }, [briefs]);

  const grouped = useMemo(() => {
    const map = {};
    withCoords.forEach(b => {
      const key = `${b.lat},${b.lng}`;
      if (!map[key]) map[key] = [];
      map[key].push(b);
    });
    return map;
  }, [withCoords]);

  const BUDGET_COLS = [
    { label: '< $20K',       color: '#F97316', bg: '#FFF7ED', border: '#FED7AA', filter: b => toUSD((b.budget_min+b.budget_max)/2, b.currency) <= 20000 },
    { label: '$20K – $50K',  color: '#3B82F6', bg: '#EFF6FF', border: '#BFDBFE', filter: b => { const v = toUSD((b.budget_min+b.budget_max)/2, b.currency); return v > 20000 && v <= 50000; } },
    { label: '$50K – $100K', color: '#22C55E', bg: '#F0FDF4', border: '#BBF7D0', filter: b => { const v = toUSD((b.budget_min+b.budget_max)/2, b.currency); return v > 50000 && v <= 100000; } },
    { label: '$100K – $200K',color: '#EF4444', bg: '#FEF2F2', border: '#FECACA', filter: b => { const v = toUSD((b.budget_min+b.budget_max)/2, b.currency); return v > 100000 && v <= 200000; } },
    { label: '$200K+',       color: '#D3AF37', bg: '#FAF6D0', border: '#DFC844', filter: b => toUSD((b.budget_min+b.budget_max)/2, b.currency) > 200000 },
  ];

  return (
    <div className="space-y-5">
      {/* ── Map + Right Panel ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Map — 2/3 width */}
        <div className="lg:col-span-2 relative rounded-2xl overflow-hidden border border-gray-200 shadow-sm" style={{ height: '420px' }}>
          <MapContainer center={[30, 15]} zoom={3} style={{ height: '100%', width: '100%' }} scrollWheelZoom zoomControl>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              subdomains="abcd"
              maxZoom={20}
            />
            {Object.entries(grouped).map(([, groupBriefs]) =>
              groupBriefs.map((brief, i) => {
                const color = getBudgetColor(brief.budget_min || 0, brief.budget_max || 0, brief.currency);
                return (
                  <CircleMarker
                    key={brief.id}
                    center={[brief.lat + i * 0.22, brief.lng + i * 0.22]}
                    radius={13}
                    pathOptions={{ fillColor: color, color: 'white', weight: 2.5, opacity: 1, fillOpacity: 0.92 }}
                  >
                    <Popup maxWidth={230}>
                      <div style={{ fontFamily: 'inherit', padding: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                          <span style={{ fontSize: '20px' }}>{COUNTRY_FLAG[brief.country] || '🌍'}</span>
                          <div>
                            <p style={{ fontWeight: 700, fontSize: '13px', color: '#111', margin: 0 }}>{brief.city}</p>
                            <p style={{ fontSize: '11px', color: '#888', margin: 0 }}>{brief.country} · {formatMonth(brief.month)}</p>
                          </div>
                        </div>
                        <div style={{ marginBottom: '8px', padding: '6px 8px', background: '#f8f8f8', borderRadius: '8px' }}>
                          <p style={{ fontSize: '10px', color: '#999', margin: '0 0 2px 0', textTransform: 'uppercase' }}>Show</p>
                          <p style={{ fontSize: '12px', fontWeight: 700, color: '#222', margin: 0 }}>{brief.show_masked} {!isLoggedIn && '🔒'}</p>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                          <div style={{ flex: 1, padding: '5px 7px', borderRadius: '7px', background: color + '18', border: `1px solid ${color}33` }}>
                            <p style={{ fontSize: '9px', color: '#888', margin: '0 0 2px 0', textTransform: 'uppercase' }}>Budget</p>
                            <p style={{ fontSize: '11px', fontWeight: 700, color: color, margin: 0 }}>{formatBudget(brief.budget_min, brief.budget_max, brief.currency)}</p>
                          </div>
                          {brief.stand_w && brief.stand_d && (
                            <div style={{ padding: '5px 7px', borderRadius: '7px', background: '#f0f0f0' }}>
                              <p style={{ fontSize: '9px', color: '#888', margin: '0 0 2px 0', textTransform: 'uppercase' }}>Stand</p>
                              <p style={{ fontSize: '11px', fontWeight: 700, color: '#444', margin: 0 }}>{brief.stand_w}×{brief.stand_d}m</p>
                            </div>
                          )}
                        </div>
                        {isLoggedIn ? (
                          <a href="/admin-login" style={{ display: 'block', textAlign: 'center', background: '#16a34a', color: 'white', fontWeight: 700, fontSize: '12px', padding: '8px 12px', borderRadius: '8px', textDecoration: 'none' }}>
                            Submit Proposal →
                          </a>
                        ) : (
                          <button onClick={() => {}} style={{ width: '100%', background: '#f4f4f4', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', fontWeight: 600, color: '#555', cursor: 'pointer' }}>
                            🔒 Sign In to View Details
                          </button>
                        )}
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })
            )}
          </MapContainer>
          <BudgetLegend />
        </div>

        {/* Right Panel — Last 24h */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col" style={{ height: '420px' }}>
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <p className="text-sm font-bold text-gray-800">Recently Added</p>
            <span className="ml-auto text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Last 24h</span>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
            {last24h.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <p className="text-sm text-gray-400">No new projects in the last 24 hours</p>
                <p className="text-xs text-gray-300 mt-1">Check the Grid view for all projects</p>
              </div>
            ) : (
              last24h.map(brief => {
                const color = getBudgetColor(brief.budget_min || 0, brief.budget_max || 0, brief.currency);
                return (
                  <div key={brief.id} onClick={!isLoggedIn ? onLockedClick : undefined} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors group">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center text-base shrink-0 bg-gray-50">
                      {COUNTRY_FLAG[brief.country] || '🌍'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-800 truncate group-hover:text-green-700 transition-colors">{brief.show_masked} {!isLoggedIn && '🔒'}</p>
                      <p className="text-xs text-gray-400">{brief.city}, {brief.country}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-xs font-bold" style={{ color }}>{formatBudget(brief.budget_min, brief.budget_max, brief.currency)}</p>
                      <p className="text-xs text-gray-300">{brief.stand_w && brief.stand_d ? `${brief.stand_w}×${brief.stand_d}m` : '—'}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="px-4 py-2.5 border-t border-gray-100">
            <button
              onClick={onViewAll}
              className="w-full text-xs font-semibold text-green-700 hover:text-green-900 transition-colors text-center"
            >
              View all projects →
            </button>
          </div>
        </div>
      </div>

      {/* ── Budget Columns (5 categories × top 5 projects) ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-sm font-bold text-gray-900">Projects by Budget Range</p>
            <p className="text-xs text-gray-400 mt-0.5">Top 5 per category — sorted highest to lowest</p>
          </div>
          <DollarSign className="w-5 h-5 text-gray-300" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
          {BUDGET_COLS.map(col => {
            const colBriefs = briefs
              .filter(col.filter)
              .sort((a, b) => ((b.budget_max || 0) - (a.budget_max || 0)))
              .slice(0, 5);
            return (
              <div key={col.label} className="rounded-xl border overflow-hidden" style={{ borderColor: col.border }}>
                <div className="px-3 py-2.5 flex items-center gap-2" style={{ backgroundColor: col.bg }}>
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: col.color }} />
                  <p className="text-xs font-bold flex-1 min-w-0 leading-tight" style={{ color: col.color }}>{col.label}</p>
                  <span className="shrink-0 text-xs font-semibold text-gray-400 bg-white/70 px-1.5 py-0.5 rounded-full">{colBriefs.length}</span>
                </div>
                <div className="divide-y divide-gray-50">
                  {colBriefs.length === 0 ? (
                    <div className="px-3 py-5 text-center">
                      <p className="text-xs text-gray-300">No projects</p>
                    </div>
                  ) : (
                    colBriefs.map((brief, idx) => (
                      <div
                        key={brief.id}
                        onClick={!isLoggedIn ? onLockedClick : undefined}
                        className="px-3 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer group"
                      >
                        <div className="flex items-center gap-1.5 mb-1 min-w-0">
                          <span className="text-xs font-bold text-gray-300 shrink-0">#{idx + 1}</span>
                          <span className="text-sm shrink-0">{COUNTRY_FLAG[brief.country] || '🌍'}</span>
                          <span className="text-xs text-gray-500 min-w-0 overflow-hidden whitespace-nowrap text-ellipsis">{brief.city}</span>
                        </div>
                        <p className="text-xs font-semibold text-gray-800 break-words w-full group-hover:text-green-700 transition-colors leading-tight mb-1">
                          {brief.show_masked}
                          {!isLoggedIn && <span className="text-gray-300 ml-0.5">🔒</span>}
                        </p>
                        <p className="text-xs font-bold break-words" style={{ color: col.color }}>
                          {formatBudget(brief.budget_min, brief.budget_max, brief.currency)}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ContractorDiscover() {
  const navigate = useNavigate();
  const contentRef = useRef(null);

  // Data
  const [mapBriefs, setMapBriefs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // UI
  const [viewMode, setViewMode] = useState('map');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [budgetTier, setBudgetTier] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [standSizeFilter, setStandSizeFilter] = useState('all');

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const isLoggedIn = Boolean(token);

  // Load ALL briefs once on mount
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/briefs`, { params: { limit: 500 } })
      .then(r => {
        setMapBriefs(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => { setMapBriefs([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, []);

  // Client-side filtered + sorted briefs (non-verified tabs)
  const filteredBriefs = useMemo(() => {
    let result = [...mapBriefs];

    // Text search: city, country name, venue, show name
    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();
      result = result.filter(b => {
        const cityLower = (b.city || '').toLowerCase();
        const countryCode = (b.country || '').toLowerCase();
        const countryName = (COUNTRY_NAMES[b.country] || '').toLowerCase();
        const venueKeywords = (CITY_VENUES[b.city] || '').toLowerCase();
        const showMasked = (b.show_masked || '').toLowerCase();
        return (
          cityLower.includes(q) ||
          countryCode.includes(q) ||
          countryName.includes(q) ||
          venueKeywords.includes(q) ||
          showMasked.includes(q)
        );
      });
    }

    // Country filter
    if (countryFilter) {
      result = result.filter(b => b.country === countryFilter);
    }

    // Budget tier
    if (budgetTier !== 'all') {
      result = result.filter(b => getBudgetTierKey(b.budget_min || 0, b.budget_max || 0, b.currency) === budgetTier);
    }

    // Stand size
    if (standSizeFilter !== 'all') {
      result = result.filter(b => {
        const sqm = b.stand_area || (b.stand_w && b.stand_d ? b.stand_w * b.stand_d : 0);
        if (standSizeFilter === '<50') return sqm < 50;
        if (standSizeFilter === '50-100') return sqm >= 50 && sqm < 100;
        if (standSizeFilter === '100-200') return sqm >= 100 && sqm < 200;
        if (standSizeFilter === '200+') return sqm >= 200;
        return true;
      });
    }

    // Sort
    if (sortBy === 'budget_high') {
      result.sort((a, b) => toUSD(b.budget_max || 0, b.currency) - toUSD(a.budget_max || 0, a.currency));
    } else if (sortBy === 'budget_low') {
      result.sort((a, b) => toUSD(a.budget_min || 0, a.currency) - toUSD(b.budget_min || 0, b.currency));
    } else if (sortBy === 'deadline') {
      result.sort((a, b) => {
        const da = a.deadline_at ? new Date(a.deadline_at).getTime() : Infinity;
        const db2 = b.deadline_at ? new Date(b.deadline_at).getTime() : Infinity;
        return da - db2;
      });
    }
    // default: newest — already sorted by API

    return result;
  }, [mapBriefs, searchText, countryFilter, budgetTier, standSizeFilter, sortBy]);

  const displayedBriefs = filteredBriefs.slice(0, displayCount);
  const hasMore = filteredBriefs.length > displayCount;

  // Verified briefs — only is_verified=true, with same filters applied
  const verifiedBriefs = useMemo(() => {
    let result = mapBriefs.filter(b => b.is_verified === true);
    // Apply text/country/budget/stand filters
    if (searchText.trim()) {
      const q = searchText.toLowerCase().trim();
      result = result.filter(b => {
        const cityLower = (b.city || '').toLowerCase();
        const countryCode = (b.country || '').toLowerCase();
        const countryName = (COUNTRY_NAMES[b.country] || '').toLowerCase();
        const venueKeywords = (CITY_VENUES[b.city] || '').toLowerCase();
        const showMasked = (b.show_masked || '').toLowerCase();
        return cityLower.includes(q) || countryCode.includes(q) || countryName.includes(q) || venueKeywords.includes(q) || showMasked.includes(q);
      });
    }
    if (countryFilter) result = result.filter(b => b.country === countryFilter);
    if (budgetTier !== 'all') result = result.filter(b => getBudgetTierKey(b.budget_min || 0, b.budget_max || 0, b.currency) === budgetTier);
    if (standSizeFilter !== 'all') {
      result = result.filter(b => {
        const sqm = b.stand_area || (b.stand_w && b.stand_d ? b.stand_w * b.stand_d : 0);
        if (standSizeFilter === '<50') return sqm < 50;
        if (standSizeFilter === '50-100') return sqm >= 50 && sqm < 100;
        if (standSizeFilter === '100-200') return sqm >= 100 && sqm < 200;
        if (standSizeFilter === '200+') return sqm >= 200;
        return true;
      });
    }
    if (sortBy === 'budget_high') result.sort((a, b) => toUSD(b.budget_max || 0, b.currency) - toUSD(a.budget_max || 0, a.currency));
    else if (sortBy === 'budget_low') result.sort((a, b) => toUSD(a.budget_min || 0, a.currency) - toUSD(b.budget_min || 0, b.currency));
    else if (sortBy === 'deadline') result.sort((a, b) => {
      const da = a.deadline_at ? new Date(a.deadline_at).getTime() : Infinity;
      const db2 = b.deadline_at ? new Date(b.deadline_at).getTime() : Infinity;
      return da - db2;
    });
    return result;
  }, [mapBriefs, searchText, countryFilter, budgetTier, standSizeFilter, sortBy]);

  const allVerified = mapBriefs.filter(b => b.is_verified === true);
  const displayedVerified = verifiedBriefs.slice(0, displayCount);
  const hasMoreVerified = verifiedBriefs.length > displayCount;

  const handleViewAll = () => {
    setViewMode('grid');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleResetFilters = () => {
    setSearchText('');
    setCountryFilter('');
    setBudgetTier('all');
    setSortBy('newest');
    setStandSizeFilter('all');
    setDisplayCount(PAGE_SIZE);
  };

  const TABS = [
    { id: 'map',      label: 'Map',              Icon: Map },
    { id: 'grid',     label: 'Grid',             Icon: LayoutGrid },
    { id: 'list',     label: 'List',             Icon: List },
    { id: 'verified', label: 'Verified Members', Icon: BadgeCheck },
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-slate-900">
      {showLoginModal && <LoginPromptModal onClose={() => setShowLoginModal(false)} />}

      {/* HEADER */}
      <header className="bg-white border-b border-gray-100 px-4 md:px-6 py-4 sticky top-0 z-[500] shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <img src="https://boothshop.my.canva.site/master-file/images/f240dd7de334359f1526183865a27145.svg" alt="EXPO STAND" className="w-8 h-7" />
            <div>
              <div className="text-sm font-bold leading-tight text-gray-800">EXPO STAND</div>
              <div className="text-xs text-gray-400">Discover Projects</div>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <Link to="/contractor/landing" className="hidden sm:flex items-center gap-1.5 text-sm text-gray-600 hover:text-green-700 transition-colors">
              <ChevronLeft className="w-4 h-4" />Contractor Portal
            </Link>
            {isLoggedIn ? (
              <button onClick={() => navigate('/app/contractor/dashboard')} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors">
                Dashboard
              </button>
            ) : (
              <Link to="/admin-login" className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors">
                <LogIn className="w-3.5 h-3.5" />Sign In
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* HERO BANNER */}
      <div className="bg-gradient-to-br from-green-800 to-green-600 text-white py-10 md:py-14 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-4">
            <Globe className="w-4 h-4" />
            {total > 0 ? `${total} active projects found` : 'Loading projects...'}
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3">Open Exhibition Projects</h1>
          <p className="text-green-100 max-w-xl mx-auto text-base md:text-lg">
            Discover exhibitors worldwide looking for stand contractors and submit your proposal.
          </p>
          {!isLoggedIn && (
            <div className="mt-6 inline-flex items-center gap-2 bg-amber-400/20 border border-amber-300/40 rounded-xl px-4 py-2.5 text-sm">
              <Lock className="w-4 h-4 text-amber-300" />
              <span className="text-amber-100">Show names are hidden — <strong>Sign in</strong> to see full details</span>
            </div>
          )}
        </div>
      </div>

      {/* VIEW MODE TABS */}
      <div className="bg-white border-b border-gray-100 px-4 shadow-sm" ref={contentRef}>
        <div className="max-w-7xl mx-auto flex items-center overflow-x-auto">
          {TABS.map(tab => {
            const Icon = tab.Icon;
            const active = viewMode === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { setViewMode(tab.id); setDisplayCount(PAGE_SIZE); }}
                className={`flex items-center gap-2 px-4 py-4 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap shrink-0 ${active ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-200'}`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className={`w-4 h-4 ${active ? 'text-green-600' : ''}`} />
                {tab.label}
                {tab.id === 'verified' && (
                  <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full ml-1 flex items-center gap-0.5" style={{ backgroundColor: '#FAF6D0', color: '#D3AF37' }}>
                    <BadgeCheck className="w-2.5 h-2.5" />Gold
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {(viewMode === 'grid' || viewMode === 'list' || viewMode === 'verified') && (
        <AdvancedFilterBar
          searchText={searchText} setSearchText={setSearchText}
          countryFilter={countryFilter} setCountryFilter={setCountryFilter}
          budgetTier={budgetTier} setBudgetTier={setBudgetTier}
          sortBy={sortBy} setSortBy={setSortBy}
          standSizeFilter={standSizeFilter} setStandSizeFilter={setStandSizeFilter}
          onReset={handleResetFilters}
          filteredCount={viewMode === 'verified' ? verifiedBriefs.length : filteredBriefs.length}
          totalCount={viewMode === 'verified' ? allVerified.length : mapBriefs.length}
        />
      )}

      {/* CONTENT */}
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* KPI Cards — different for Verified tab */}
        {!loading && mapBriefs.length > 0 && viewMode !== 'verified' && (
          <KPIBar briefs={mapBriefs} total={total} isVerified={false} />
        )}
        {!loading && viewMode === 'verified' && allVerified.length > 0 && (
          <KPIBar briefs={allVerified} total={allVerified.length} isVerified={true} />
        )}

        <AnimatePresence mode="wait">

          {/* MAP VIEW */}
          {viewMode === 'map' && (
            <motion.div key="map" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
                </div>
              ) : (
                <MapDashboard
                  briefs={mapBriefs}
                  isLoggedIn={isLoggedIn}
                  onLockedClick={() => setShowLoginModal(true)}
                  onViewAll={handleViewAll}
                />
              )}
            </motion.div>
          )}

          {/* GRID VIEW */}
          {viewMode === 'grid' && (
            <motion.div key="grid" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
                </div>
              ) : filteredBriefs.length === 0 ? (
                <div className="text-center py-20">
                  <Briefcase className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                  <p className="text-lg font-semibold text-gray-500">No projects match your filters</p>
                  <p className="text-sm text-gray-400 mt-1 mb-4">Try adjusting your search criteria</p>
                  <button onClick={handleResetFilters} className="text-sm text-green-600 hover:text-green-800 font-semibold underline">Clear all filters</button>
                </div>
              ) : (
                <>
                  {!isLoggedIn && <p className="text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg mb-4">Show names are hidden — Sign in for full details</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {displayedBriefs.map((brief, idx) => (
                      <BriefCard key={brief.id} brief={brief} isLoggedIn={isLoggedIn} onLockedClick={() => setShowLoginModal(true)} index={idx} />
                    ))}
                  </div>
                  {hasMore && (
                    <div className="flex justify-center mt-10">
                      <button
                        onClick={() => setDisplayCount(c => c + PAGE_SIZE)}
                        className="flex items-center gap-2 px-8 py-3 bg-white border border-gray-200 rounded-2xl text-sm font-semibold text-gray-700 hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-all shadow-sm"
                      >
                        <ChevronRight className="w-4 h-4 rotate-90" />
                        Load more ({filteredBriefs.length - displayCount} remaining)
                      </button>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}

          {/* LIST VIEW */}
          {viewMode === 'list' && (
            <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
                </div>
              ) : filteredBriefs.length === 0 ? (
                <div className="text-center py-20">
                  <Briefcase className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                  <p className="text-lg font-semibold text-gray-500">No projects match your filters</p>
                  <button onClick={handleResetFilters} className="text-sm text-green-600 hover:text-green-800 font-semibold underline mt-2">Clear all filters</button>
                </div>
              ) : (
                <>
                  {!isLoggedIn && <p className="text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg mb-4">Show names hidden — Sign in for details</p>}
                  <div className="space-y-2.5">
                    {displayedBriefs.map((brief, idx) => (
                      <BriefRow key={brief.id} brief={brief} isLoggedIn={isLoggedIn} onLockedClick={() => setShowLoginModal(true)} index={idx} />
                    ))}
                  </div>
                  {hasMore && (
                    <div className="flex justify-center mt-10">
                      <button
                        onClick={() => setDisplayCount(c => c + PAGE_SIZE)}
                        className="flex items-center gap-2 px-8 py-3 bg-white border border-gray-200 rounded-2xl text-sm font-semibold text-gray-700 hover:bg-green-50 hover:border-green-300 hover:text-green-700 transition-all shadow-sm"
                      >
                        <ChevronRight className="w-4 h-4 rotate-90" />
                        Load more ({filteredBriefs.length - displayCount} remaining)
                      </button>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}

          {viewMode === 'verified' && (
            <motion.div key="verified" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#FAF6D0' }}>
                    <BadgeCheck className="w-5 h-5" style={{ color: '#D3AF37' }} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">Verified Members Only</p>
                    <p className="text-xs text-gray-500">Projects posted by EXPO STAND-verified exhibitors</p>
                  </div>
                </div>
                {!isLoggedIn && (
                  <p className="text-xs bg-amber-50 border border-amber-100 px-3 py-1.5 rounded-lg" style={{ color: '#D3AF37' }}>
                    Show names hidden — Sign in for full details
                  </p>
                )}
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#D3AF37' }} />
                </div>
              ) : verifiedBriefs.length === 0 ? (
                <div className="text-center py-20">
                  <BadgeCheck className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                  <p className="text-lg font-semibold text-gray-500">No verified projects match your filters</p>
                  <button onClick={handleResetFilters} className="text-sm font-semibold underline mt-2" style={{ color: '#D3AF37' }}>Clear filters</button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {displayedVerified.map((brief, idx) => (
                      <BriefCard key={brief.id} brief={brief} isLoggedIn={isLoggedIn} onLockedClick={() => setShowLoginModal(true)} index={idx} verified={true} />
                    ))}
                  </div>
                  {hasMoreVerified && (
                    <div className="flex justify-center mt-10">
                      <button
                        onClick={() => setDisplayCount(c => c + PAGE_SIZE)}
                        className="flex items-center gap-2 px-8 py-3 bg-white rounded-2xl text-sm font-semibold transition-all shadow-sm border hover:opacity-90"
                        style={{ borderColor: '#DFC844', color: '#D3AF37' }}
                      >
                        <ChevronRight className="w-4 h-4 rotate-90" />
                        Load more ({verifiedBriefs.length - displayCount} remaining)
                      </button>
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}

        </AnimatePresence>
      </div>

      {/* CTA Bottom */}
      {!isLoggedIn && (
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-12 px-4 mt-8">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-2">Start Submitting Proposals</h2>
            <p className="text-green-100 mb-6">Sign up free, see show names and submit proposals to live projects.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contractor/signup" className="flex items-center justify-center gap-2 bg-white text-green-700 font-bold px-6 py-3 rounded-xl hover:bg-green-50 transition-colors">
                <Building2 className="w-4 h-4" />Sign Up Free
              </Link>
              <Link to="/admin-login" className="flex items-center justify-center gap-2 bg-green-700 text-white font-semibold px-6 py-3 rounded-xl border border-green-500 hover:bg-green-800 transition-colors">
                <LogIn className="w-4 h-4" />Sign In
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
